import { generateDigupSheet } from "@/features/dig-up/api";
import type {
  PipePartFilters,
  GradientMode,
  PipePart,
} from "@/types/pipeParts";
import { i18n } from "@/config/i18n";
import { unitsConverter } from "@/config/units";
import {
  DigUpSelector,
  getDataForDigupRequest,
} from "@/features/dig-up/helpers";
import {
  getMinMaxValue,
  getUniqueValues,
  rangeFiltering,
  valueFiltering,
} from "@/utils/filterHelpers";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getPipePartsOptions } from "@/pages/pipe-parts/configs";
import { DefaultService, OpenAPI } from "@/open-api-code/ili-api";
import { auth0 } from "@/config/auth0";
import { usePipelinesStore } from "./usePipelinesStore";
import { useAuthStore } from "./useAuthStore";
import { useMapStore } from "./useMapStore";
import { useAnomaliesStore } from "./useAnomaliesStore";
import { useSettingsStore } from "./useSettingsStore";
import { mergeChanges } from "@/utils/objects";
import { showToastError } from "@/utils/errors";
import type { PlotlyFilter } from "@/types/plotlyFilter";
import { GRADIENT_MODES, REMAININGS } from "@/constants";
import { useStoreUnconfirmedFilters } from "@/utils/storeHelpers";
import { PipePartsWidget } from "@/pages/pipe-parts/views/PipePartsMapmode/config";
import type { RemainingsType, WidgetsSettingsType } from "@/types/widgets";
import { shouldBeWidgetsInitMinimized } from "@/config/widgets";

const requests = new Map();

const INITIAL_PLOTLY_FILTER: PlotlyFilter = {
  property: "",
  range: [],
};
const INITIAL_PIPE_PART_FILTERS: PipePartFilters = {
  selectedDistance: [],
  selectedPipePartTypes: [],
  selectedMaterials: [],
  selectedOvalityMax: [],
  selectedOvalityMean: [],
  selectedDeformationHor: [],
  selectedDeformationVer: [],
  plotlyFilter: INITIAL_PLOTLY_FILTER,
};

export const usePipePartsStore = defineStore("pipeParts", () => {
  const mapStore = useMapStore();
  const pipelinesStore = usePipelinesStore();
  const authStore = useAuthStore();
  const anomaliesStore = useAnomaliesStore();
  const settingsStore = useSettingsStore();

  const { t } = useI18n();

  const shouldGetNewPipeParts = ref(true);

  watch(
    () => pipelinesStore.selectedInspectionId,
    () => {
      shouldGetNewPipeParts.value = true;
    }
  );

  const pipeParts = ref<PipePart[]>([]);

  const pipePartTypes = computed(() =>
    getUniqueValues(pipeParts.value, "pipe_part_type")
  );
  const materials = computed(() =>
    getUniqueValues(pipeParts.value, GRADIENT_MODES.MATERIAL)
  );
  const convertedPipeParts = computed(() => {
    const conversionConfig = {
      distance: "m",
      length: "m",
      horizontal_out_of_straight: "m",
      vertical_out_of_straight: "m",
      remaining_mean: "mm",
      remaining_min: "mm",
      remaining_max: "mm",
      remaining_std: "mm",
      degradated_total_mean: "mm",
      degradated_total_min: "mm",
      degradated_total_max: "mm",
      degradated_total_std: "mm",
      remaining_life_calculation: {
        road_distance: "m",
        ground_water_level: "m",
      },
      wall_thickness_original: "mm",
      diameter_class: "mm",
    };

    return pipeParts.value.map((pipePart) => {
      const converted = { ...pipePart };

      for (const key in conversionConfig) {
        const unit = conversionConfig[key];
        if (key in pipePart && pipePart[key] !== null) {
          converted[key] = unitsConverter.instance.convert(pipePart[key], unit);
        }
      }

      if (pipePart.remaining_life_calculation) {
        converted.remaining_life_calculation = {
          ...pipePart.remaining_life_calculation,
        };

        for (const key in conversionConfig.remaining_life_calculation) {
          const unit = conversionConfig.remaining_life_calculation[key];
          if (
            key in pipePart.remaining_life_calculation &&
            pipePart.remaining_life_calculation[key] !== null
          ) {
            converted.remaining_life_calculation[key] =
              unitsConverter.instance.convert(
                pipePart.remaining_life_calculation[key],
                unit
              );
          }
        }
      }

      return converted;
    });
  });
  const pipePartsWithWallThickness = computed(() => {
    return convertedPipeParts.value.map((pipePart) => {
      const {
        wall_thickness_remaining_mean,
        wall_thickness_remaining_min,
        wall_thickness_remaining_max,
        wall_thickness_remaining_std,
        wall_thickness_degradated_total_mean,
        wall_thickness_degradated_total_min,
        wall_thickness_degradated_total_max,
        wall_thickness_degradated_total_std,
      } = pipePart;

      const getRemainingValue = (
        thicknessArray: number[][],
        prop: RemainingsType
      ) => {
        const layerNum = wallThicknessRemaining.value[prop].layerNum;
        const fullPipe = thicknessArray[0];
        const remainingValue =
          fullPipe.length > 1 ? fullPipe[layerNum] : fullPipe[0];
        return unitsConverter.instance.convert(remainingValue, "mm");
      };

      const remaining_mean = getRemainingValue(
        wall_thickness_remaining_mean,
        REMAININGS.MEAN
      );

      const remaining_min = getRemainingValue(
        wall_thickness_remaining_min,
        REMAININGS.MIN
      );

      const remaining_max = getRemainingValue(
        wall_thickness_remaining_max,
        REMAININGS.MAX
      );

      const remaining_std = getRemainingValue(
        wall_thickness_remaining_std,
        REMAININGS.STD
      );

      return {
        ...pipePart,
        remaining_mean,
        remaining_min,
        remaining_max,
        remaining_std,
        degradated_total_mean: wall_thickness_degradated_total_mean[0],
        degradated_total_min: wall_thickness_degradated_total_min[0],
        degradated_total_max: wall_thickness_degradated_total_max[0],
        degradated_total_std: wall_thickness_degradated_total_std[0],
      };
    });
  });
  const pipePartsWithTranslatedQualityAnomaly = computed(() => {
    const influenceTypes = {
      dirt: i18n.global.t(`quality_influences.dirt`),
      gas: i18n.global.t(`quality_influences.gas`),
      misalignment: i18n.global.t(`quality_influences.misalignment`),
      unaffected: i18n.global.t(`quality_influences.unaffected`),
      corrosion_product_dq: i18n.global.t(`quality_influences.corrosion_product_dq`),
    };

    return pipePartsWithWallThickness.value.map((pipePart) => {
      if (!pipePart.quality_anomaly) {
        return { ...pipePart, quality_anomaly: null };
      }

      let influences = pipePart.quality_anomaly.split(", ");

      influences = influences
        .map((type) => influenceTypes[type.toLowerCase()])
        .join(", ");
      return { ...pipePart, quality_anomaly: influences };
    });
  });
  const minMaxDistance = computed(() =>
    getMinMaxValue(convertedPipeParts.value, "distance")
  );
  const minMaxOvalityMax = computed(() =>
    getMinMaxValue(convertedPipeParts.value, GRADIENT_MODES.OVALITY_MAX)
  );
  const minMaxOvalityMean = computed(() =>
    getMinMaxValue(convertedPipeParts.value, GRADIENT_MODES.OVALITY_MEAN)
  );
  const minMaxDeformationHor = computed(() =>
    getMinMaxValue(
      convertedPipeParts.value,
      GRADIENT_MODES.HORIZONTAL_OUT_OF_STRAIGHT
    )
  );
  const minMaxDeformationVer = computed(() =>
    getMinMaxValue(
      convertedPipeParts.value,
      GRADIENT_MODES.VERTICAL_OUT_OF_STRAIGHT
    )
  );
  const minMaxRoadDistance = computed(() => {
    if (convertedPipeParts.value[0].remaining_life_calculation) {
      return getMinMaxValue(
        convertedPipeParts.value,
        "remaining_life_calculation.road_distance" as keyof PipePart
      );
    }

    return [];
  });
  const minMaxDegradationRate = computed(() => {
    if (convertedPipeParts.value[0].remaining_life_calculation) {
      return getMinMaxValue(
        convertedPipeParts.value,
        "remaining_life_calculation.degradation_rate" as keyof PipePart
      );
    }

    return [];
  });
  const minMaxGroundWaterLevel = computed(() => {
    if (convertedPipeParts.value[0].remaining_life_calculation) {
      return getMinMaxValue(
        convertedPipeParts.value,
        "remaining_life_calculation.road_distance" as keyof PipePart
      );
    }

    return [];
  });

  function setPipeParts(newPipeParts: PipePart[]) {
    pipeParts.value = newPipeParts;
  }
  async function getPipeParts() {
    try {
      if (!pipelinesStore.selectedInspectionId)
        throw new Error("No selectedInspectionId in store");

      setLoading(true);

      OpenAPI.TOKEN = await auth0.getAccessTokenSilently();

      requests.set(
        "pipePartsRequest",
        DefaultService.readPipePartsInlineInspectionsPipePartsInspectionIdGroupGet(
          pipelinesStore.selectedInspectionId,
          authStore.selectedGroup
        )
      );

      const pipeParts = await requests.get("pipePartsRequest");
      setPipeParts(pipeParts);
    } catch (error) {
      showToastError(error);
    } finally {
      setLoading(false);
    }
  }
  async function initPipeParts() {
    await getPipeParts();
    requests.delete("pipePartsRequest");
    initFilters();
    checkWTRemainigLevels();

    shouldGetNewPipeParts.value = false;
  }

  const isLoading = ref(true);

  function setLoading(loading: boolean) {
    isLoading.value = loading;
  }

  const isAwaitingDigup = ref(false);

  function setIsAwaitingDigup(newValue: boolean) {
    isAwaitingDigup.value = newValue;
  }

  const filters = ref<PipePartFilters>(
    structuredClone(INITIAL_PIPE_PART_FILTERS)
  );

  const hasActiveFilters = computed(() => {
    return Object.entries(filters.value).some(([key, value]) => {
      if (["selectedPipePartTypes", "selectedMaterials"].includes(key)) {
        // @ts-expect-error
        return value.length > 0;
      }

      switch (key) {
        case "selectedDistance":
          return (
            minMaxDistance.value[0] !== value[0] ||
            minMaxDistance.value[1] !== value[1]
          );
        case "selectedOvalityMax":
          return (
            minMaxOvalityMax.value[0] !== value[0] ||
            minMaxOvalityMax.value[1] !== value[1]
          );
        case "selectedOvalityMean":
          return (
            minMaxOvalityMean.value[0] !== value[0] ||
            minMaxOvalityMean.value[1] !== value[1]
          );
        case "selectedDeformationHor":
          return (
            minMaxDeformationHor.value[0] !== value[0] ||
            minMaxDeformationHor.value[1] !== value[1]
          );
        case "selectedDeformationVer":
          return (
            minMaxDeformationVer.value[0] !== value[0] ||
            minMaxDeformationVer.value[1] !== value[1]
          );
      }
    });
  });
  const filteredPipeParts = computed(() => {
    const {
      selectedDistance,
      selectedPipePartTypes,
      selectedMaterials,
      selectedOvalityMax,
      selectedOvalityMean,
      selectedDeformationHor,
      selectedDeformationVer,
      plotlyFilter,
    } = filters.value;

    let filtered = pipePartsWithTranslatedQualityAnomaly.value;

    if (selectedPipePartTypes.length) {
      filtered = valueFiltering(
        filtered,
        "pipe_part_type",
        selectedPipePartTypes
      );
    }

    if (selectedMaterials.length) {
      filtered = valueFiltering(
        filtered,
        GRADIENT_MODES.MATERIAL,
        selectedMaterials
      );
    }

    filtered = rangeFiltering(filtered, "distance", selectedDistance);

    if (!mapStore.isMapMode) {
      filtered = rangeFiltering(
        filtered,
        GRADIENT_MODES.OVALITY_MAX,
        selectedOvalityMax
      );
      filtered = rangeFiltering(
        filtered,
        GRADIENT_MODES.OVALITY_MEAN,
        selectedOvalityMean
      );
      filtered = rangeFiltering(
        filtered,
        GRADIENT_MODES.HORIZONTAL_OUT_OF_STRAIGHT,
        selectedDeformationHor
      );
      filtered = rangeFiltering(
        filtered,
        GRADIENT_MODES.VERTICAL_OUT_OF_STRAIGHT,
        selectedDeformationVer
      );
    }

    if (plotlyFilter.property !== "") {
      const { property, range } = plotlyFilter;

      // histogram zoom only keeps pipe parts that have a value in the zoomed range
      // @ts-expect-error
      filtered = rangeFiltering(filtered, property, range, false);
    }

    filtered.forEach((elem) => {
      if (typeof elem.quality === "number")
        elem.quality = elem.quality.toFixed(2);
    });

    return filtered;
  });

  function setFilters(newFilters: Partial<PipePartFilters>) {
    mergeChanges(filters.value, newFilters);
    selectedPipePartId.value = null;
  }
  function resetPlotlyFilter() {
    setFilters({ plotlyFilter: structuredClone(INITIAL_PLOTLY_FILTER) });
  }
  function initFilters() {
    setFilters({
      selectedDistance: minMaxDistance.value,
      selectedOvalityMax: minMaxOvalityMax.value,
      selectedOvalityMean: minMaxOvalityMean.value,
      selectedDeformationHor: minMaxDeformationHor.value,
      selectedDeformationVer: minMaxDeformationVer.value,
    });
  }
  function resetFilters() {
    filters.value = structuredClone(INITIAL_PIPE_PART_FILTERS);
    initFilters();
  }

  const selectedPipePartId = ref<string | null>(null);

  const selectedPipePart = computed(() => {
    if (!selectedPipePartId.value) return;

    return pipePartsWithWallThickness.value.find(
      ({ id }) => id === selectedPipePartId.value
    );
  });
  const anomaliesByPipePart = computed(() => {
    if (!selectedPipePart.value) {
      return [];
    }

    return anomaliesStore.anomalies.filter((anomaly) =>
      anomaly.pipe_parts.some(
        (pipePart) => pipePart.id === selectedPipePart.value?.id
      )
    );
  });

  function setSelectedPipePartId(newSelectedPipePartId: string) {
    selectedPipePartId.value = newSelectedPipePartId;
  }

  const selectedGradientMode = ref<GradientMode>(
    GRADIENT_MODES.DEGRADATION_LEVEL
  );
  function setSelectedGradientMode(newGradientMode: GradientMode) {
    selectedGradientMode.value = newGradientMode;
  }

  const selectedLegend = ref(null);

  function setSelectedLegend(newLegend) {
    selectedLegend.value = newLegend;
  }
  function toggleSelectedLegend(legend) {
    if (selectedLegend.value === legend) {
      setSelectedLegend(null);
      return;
    }

    setSelectedLegend(legend);
  }

  const partsSelector = ref(new DigUpSelector(i18n.global.locale));

  async function getDigupSheet() {
    try {
      setIsAwaitingDigup(true);

      const requestData = getDataForDigupRequest(
        partsSelector.value.value,
        pipeParts.value,
        settingsStore.locale,
        settingsStore.units
      );

      const token = await auth0.getAccessTokenSilently();
      await generateDigupSheet(token, authStore.selectedGroup, requestData);
    } catch (error) {
      showToastError(error);
    } finally {
      setIsAwaitingDigup(false);
    }
  }

  const showAnomalies = ref(false);

  function setShowAnomalies(areAnomaliesVisible) {
    showAnomalies.value = areAnomaliesVisible;
  }
  function toggleAnomaliesVisibility() {
    setShowAnomalies(!showAnomalies.value);
  }

  const wallThicknessRemaining = ref({
    mean: {
      isMultipleLayers: false,
      layerNum: 0,
      length: 0,
    },
    min: {
      isMultipleLayers: false,
      layerNum: 0,
      length: 0,
    },
    max: {
      isMultipleLayers: false,
      layerNum: 0,
      length: 0,
    },
    std: {
      isMultipleLayers: false,
      layerNum: 0,
      length: 0,
    },
  });

  function setWallThicknessForLevel(payload) {
    const { level, changes } = payload;
    wallThicknessRemaining.value[level] = {
      ...wallThicknessRemaining.value[level],
      ...changes,
    };
  }
  function checkWTRemainigLevels() {
    for (const level in wallThicknessRemaining.value) {
      const levelKey = `wall_thickness_remaining_${level}`;

      const pipePartWithMultipleLayers = pipeParts.value.find(
        (pipePart) =>
          pipePart.hasOwnProperty(levelKey) &&
          pipePart[levelKey][0].length > 1 &&
          pipePart.material_layers.length
      );

      const levelHasMultipleLayers = pipePartWithMultipleLayers !== undefined;
      let newLevelLength = 1;
      let newLayerNum = 0;

      if (levelHasMultipleLayers) {
        newLevelLength = pipePartWithMultipleLayers[levelKey][0].length;
        newLayerNum = 1;
      }

      const levelChanges = {
        isMultipleLayers: levelHasMultipleLayers,
        layerNum: newLayerNum,
        length: newLevelLength,
      };

      setWallThicknessForLevel({ level, changes: levelChanges });
    }
  }

  const gradientOptions = computed(() => {
    function checkDisabledColorMode(mode: GradientMode) {
      if (!mode) return true;

      if (mode === GRADIENT_MODES.WIRE_BREAKS) {
        const disabled = !pipeParts.value.some(
          (part) => !!part?.wire_breaks_count
        );
        return disabled;
      }

      if (mode === GRADIENT_MODES.DEGRADATION_LEVEL) {
        const disabled = !pipeParts.value.some(
          (part) => part.degradation_level !== 0
        );
        return disabled;
      }

      if (mode === GRADIENT_MODES.WALL_THICKNESS_ORIGINAL) {
        const disable = !pipePartsWithTranslatedQualityAnomaly.value.some(
          (pipePart) => !!pipePart[GRADIENT_MODES.WALL_THICKNESS_ORIGINAL]
        );
        return disable;
      }

      const keys = mode.split(".");
      if (keys.length === 1) {
        const hasValues = !!pipePartsWithTranslatedQualityAnomaly.value.find(
          (pipePart) =>
            pipePart[keys[0]] !== null && pipePart[keys[0]] !== undefined
        );

        return !hasValues;
      } else {
        const hasValues = !!pipePartsWithTranslatedQualityAnomaly.value.find(
          (pipePart) =>
            pipePart[keys[0]][keys[1]] !== null &&
            pipePart[keys[0]][keys[1]] !== undefined
        );
        return !hasValues;
      }
    }

    const hasRemainingLife = pipePartsWithTranslatedQualityAnomaly.value.some(
      ({ remaining_life_calculation }) => !!remaining_life_calculation
    );

    const options = getPipePartsOptions(
      hasRemainingLife,
      t,
      unitsConverter.instance
    );

    const checkedOptions = options.map((option) => ({
      ...option,
      disabled: pipePartsWithTranslatedQualityAnomaly.value
        ? checkDisabledColorMode(option.value)
        : false,
    }));

    return checkedOptions;
  });
  const histogramTitle = computed(
    () =>
      gradientOptions.value.find(
        (opt) => opt.value === selectedGradientMode.value
      )?.title
  );
  const showGradient = computed(() => {
    const modesWithoutGradient: GradientMode[] = [
      GRADIENT_MODES.QUALITY_ANOMALY,
      GRADIENT_MODES.DEGRADATION_LEVEL,
      GRADIENT_MODES.REMAINING_LIFE_GROUP,
      GRADIENT_MODES.MATERIAL,
      GRADIENT_MODES.ROAD_TYPE,
      GRADIENT_MODES.SOIL_TYPE,
    ];

    return !modesWithoutGradient.includes(selectedGradientMode.value);
  });

  watch(gradientOptions, (options) => {
    if (options?.length) {
      const isSelectedGradientModeDisabled = options.find(
        (option) => option.value === selectedGradientMode.value
      )?.disabled;
      if (isSelectedGradientModeDisabled) {
        const newSelectedGradientMode = options.find(
          (option) => !option.disabled
        )?.value;
        setSelectedGradientMode(newSelectedGradientMode);
      }
    }
  });

  const wmsLayers = ref([]);

  const widgetsMapSettings = ref<WidgetsSettingsType>({
    [PipePartsWidget.PipePartsTable]: {
      key: PipePartsWidget.PipePartsTable,
      title: t("pipe_parts.widgets.table.maximize_title"),
      initialWidth: 780,
      initialHeight: 355,
      getInitialX(container) {
        return container.offsetWidth - ((this.initialWidth as number) + 10);
      },
      getInitialY: () => 150,
      initiallyMinimized: shouldBeWidgetsInitMinimized,
    },

    [PipePartsWidget.Histogram]: {
      key: PipePartsWidget.Histogram,
      title: histogramTitle,
      initialWidth: 655,
      initialHeight: 400,
      getInitialX: () => 5,
      getInitialY(container) {
        return container.offsetHeight - ((this.initialHeight as number) + 35);
      },
      initiallyMinimized: shouldBeWidgetsInitMinimized,
    },

    [PipePartsWidget.HeightProfileChart]: {
      key: PipePartsWidget.HeightProfileChart,
      title: t("pipe_parts.widgets.heightProfile.maximize_title"),
      initialWidth: 655,
      initialHeight: 400,
      getInitialX: () => 5,
      getInitialY(container) {
        return container.offsetHeight - ((this.initialHeight as number) + 180);
      },
      initiallyMinimized: true,
    },

    [PipePartsWidget.Radar]: {
      key: PipePartsWidget.Radar,
      title: t("pipe_parts.widgets.radar.maximize_title"),
      initialWidth: 400,
      initialHeight: 400,
      getInitialX(container) {
        return container.offsetWidth - ((this.initialWidth as number) + 5);
      },
      getInitialY(container) {
        return container.offsetHeight - ((this.initialHeight as number) + 45);
      },
      initiallyMinimized: true,
    },

    [PipePartsWidget.GradientLegend]: {
      key: PipePartsWidget.GradientLegend,
      title: t("joints.widgets.gradient.maximize_title"),
      initialWidth: 130,
      initialHeight: "auto",
      getInitialX: () => 5,
      getInitialY: () => 50,
      initiallyMinimized: false,
      hidden: computed(() => !showGradient.value),
      resizable: false,
      minimizable: false,
      draggable: false,
    },

    [PipePartsWidget.ColorsLegend]: {
      key: PipePartsWidget.ColorsLegend,
      title: histogramTitle,
      initialWidth: 230,
      initialHeight: "auto",
      getInitialX: () => 5,
      getInitialY: () => 50,
      initiallyMinimized: false,
      hidden: computed(() => showGradient.value),
      resizable: false,
      minimizable: false,
      draggable: false,
    },

    [PipePartsWidget.WMSWidget]: {
      key: PipePartsWidget.WMSWidget,
      title: t("dashboard.widgets.color.maximize_title"),
      initialWidth: 350,
      initialHeight: "auto",
      getInitialX(container) {
        return (container.offsetWidth - (this.initialWidth as number)) / 2;
      },
      getInitialY: () => 5,
      initiallyMinimized: false,
      resizable: false,
    },

    [PipePartsWidget.WMSLegend]: {
      key: PipePartsWidget.WMSLegend,
      title: t("wms_layers.layers"),
      initialWidth: 250,
      initialHeight: 365,
      getInitialX: () => 5,
      getInitialY: () => 180,
      initiallyMinimized: false,
      hidden: wmsLayers.value.length === 0,
    },
  });
  function updateWidgetsMapSettings(
    newSettings: typeof widgetsMapSettings.value
  ) {
    Object.entries(newSettings).forEach(([widgetName, widgetNewSettings]) => {
      Object.keys(widgetNewSettings).forEach((key) => {
        widgetsMapSettings.value[widgetName][key] = widgetNewSettings[key];
      });
    });
  }

  function cancelAllRequests() {
    for (const request of requests.values()) {
      request.cancel();
    }
  }

  const {
    unconfirmedFilters,
    setUnconfirmedFilters,
    confirmUnconfirmedFilters,
    areNewFiltersUnconfirmed,
  } = useStoreUnconfirmedFilters(filters);

  watch(
    minMaxDistance,
    (minMax) => {
      setUnconfirmedFilters({ selectedDistance: minMax });
      confirmUnconfirmedFilters();
    },
    { deep: true }
  );

  return {
    shouldGetNewPipeParts,
    pipeParts,
    isLoading,
    setLoading,
    isAwaitingDigup,
    setIsAwaitingDigup,
    filters,
    selectedPipePartId,
    selectedGradientMode,
    selectedLegend,
    partsSelector,
    getDigupSheet,
    showAnomalies,
    wallThicknessRemaining,
    pipePartTypes,
    materials,
    convertedPipeParts,
    pipePartsWithWallThickness,
    pipePartsWithTranslatedQualityAnomaly,
    minMaxDistance,
    minMaxOvalityMax,
    minMaxOvalityMean,
    minMaxDeformationHor,
    minMaxDeformationVer,
    minMaxRoadDistance,
    minMaxDegradationRate,
    minMaxGroundWaterLevel,
    selectedPipePart,
    anomaliesByPipePart,
    hasActiveFilters,
    filteredPipeParts,
    setPipeParts,
    getPipeParts,
    initPipeParts,
    setFilters,
    resetPlotlyFilter,
    initFilters,
    resetFilters,
    setSelectedPipePartId,
    setSelectedGradientMode,
    setSelectedLegend,
    toggleSelectedLegend,
    setShowAnomalies,
    toggleAnomaliesVisibility,
    setWallThicknessForLevel,
    checkWTRemainigLevels,
    gradientOptions,
    histogramTitle,
    wmsLayers,
    widgetsMapSettings,
    updateWidgetsMapSettings,
    cancelAllRequests,
    unconfirmedFilters,
    setUnconfirmedFilters,
    confirmUnconfirmedFilters,
    areNewFiltersUnconfirmed,
  };
});
